import React from "react";
import { X, BookOpen, Plus, Check, MapPin, AlertTriangle } from "lucide-react";
import { MUSCLE_LABELS } from "./BodyDiagram";
import { getMuscleInfo } from "../data/muscleAnatomy";

/**
 * Slide-out info card for a clicked muscle.
 * Right drawer on >=md, bottom sheet on mobile.
 *
 * Props:
 *   muscleId       — id to display (or null = closed)
 *   selected       — current selection array (chip is "added" when included)
 *   onClose        — close handler
 *   onToggleSelect — toggle this muscle in selection
 *   learningMode   — controlled boolean
 *   onLearningMode — setter
 */
export default function AnatomyInfoPanel({
  muscleId,
  selected,
  onClose,
  onToggleSelect,
  learningMode,
  onLearningMode,
}) {
  const info = muscleId ? getMuscleInfo(muscleId) : null;
  const open = Boolean(muscleId);
  const isSelected = muscleId && selected.includes(muscleId);
  const name = muscleId ? MUSCLE_LABELS[muscleId] || muscleId : "";

  if (!open) return null;

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="md:hidden fixed inset-0 bg-ink-900/30 z-40"
        onClick={onClose}
        data-testid="anatomy-panel-backdrop"
      />
      <aside
        data-testid="anatomy-info-panel"
        className={[
          "fixed z-50 bg-white border border-paper-200 shadow-2xl",
          // Mobile: bottom sheet
          "left-0 right-0 bottom-0 rounded-t-3xl max-h-[80vh] overflow-y-auto",
          // Desktop: right drawer
          "md:left-auto md:right-6 md:top-24 md:bottom-6 md:w-[420px] md:rounded-3xl md:max-h-none",
        ].join(" ")}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-paper-200 px-5 py-4 flex items-start justify-between gap-3 z-10 rounded-t-3xl">
          <div className="min-w-0">
            <div className="label-overline text-terracotta-600 mb-1 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> {info?.region || "Muscle"}
            </div>
            <h2 className="font-heading text-xl text-ink-900 truncate" data-testid="panel-muscle-name">
              {name}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="anatomy-panel-close"
            className="shrink-0 p-2 rounded-full hover:bg-paper-100 text-ink-500"
            aria-label="Close anatomy panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {!info ? (
          <div className="p-5 text-sm text-ink-500">No clinical reference available for this muscle yet.</div>
        ) : (
          <div className="p-5 space-y-5 text-sm">
            {/* Add-to-treatment toggle */}
            <button
              onClick={() => onToggleSelect(muscleId)}
              data-testid="panel-toggle-select"
              className={[
                "w-full min-h-[44px] rounded-full font-medium transition-all inline-flex items-center justify-center gap-2",
                isSelected
                  ? "bg-terracotta-500 text-white hover:bg-terracotta-600"
                  : "bg-sage-500 text-white hover:bg-sage-600",
              ].join(" ")}
            >
              {isSelected ? (
                <>
                  <Check className="w-4 h-4" /> Added to treatment — tap to remove
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> Add to treatment
                </>
              )}
            </button>

            {/* Learning mode toggle */}
            <label className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-paper-50 border border-paper-200 cursor-pointer">
              <span className="inline-flex items-center gap-2 text-ink-900 font-medium">
                <BookOpen className="w-4 h-4 text-sage-600" /> Learning mode
              </span>
              <input
                type="checkbox"
                checked={learningMode}
                onChange={(e) => onLearningMode(e.target.checked)}
                data-testid="learning-mode-toggle"
                className="w-4 h-4 accent-sage-600"
              />
            </label>

            <InfoBlock label="Actions" items={info.actions} testid="panel-actions" />
            <InfoBlock label="Common pain referral" items={info.painReferral} testid="panel-pain-referral" />
            <InfoBlock label="Common restrictions" items={info.restrictions} testid="panel-restrictions" />
            <InfoBlock label="Common compensations" items={info.compensations} testid="panel-compensations" />
            <RelatedBlock items={info.related} />
            <InfoBlock label="Treatment considerations" items={info.treatment} testid="panel-treatment" />
            {info.cautions?.length > 0 && (
              <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-4">
                <div className="label-overline text-terracotta-600 mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> Cautions
                </div>
                <ul className="space-y-1.5 text-ink-800" data-testid="panel-cautions">
                  {info.cautions.map((c, i) => (
                    <li key={`caution-${i}-${c.slice(0, 12)}`} className="leading-snug">• {c}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Learning mode extras */}
            {learningMode && (
              <div className="space-y-4 pt-2 border-t border-paper-200">
                <div className="label-overline text-sage-600">Learning mode</div>
                {info.origin && (
                  <KeyValue label="Origin" value={info.origin} testid="panel-origin" />
                )}
                {info.insertion && (
                  <KeyValue label="Insertion" value={info.insertion} testid="panel-insertion" />
                )}
                {info.joints?.length > 0 && (
                  <InfoBlock label="Joints crossed" items={info.joints} testid="panel-joints" />
                )}
                {info.clinical && (
                  <KeyValue label="Clinical relevance" value={info.clinical} testid="panel-clinical" />
                )}
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function InfoBlock({ label, items, testid }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="label-overline mb-2">{label}</div>
      <ul className="space-y-1.5 text-ink-800" data-testid={testid}>
        {items.map((item, i) => (
          <li key={`${label}-${i}-${item.slice(0, 12)}`} className="leading-snug">• {item}</li>
        ))}
      </ul>
    </div>
  );
}

function KeyValue({ label, value, testid }) {
  return (
    <div>
      <div className="label-overline mb-1">{label}</div>
      <div className="text-ink-800 leading-snug" data-testid={testid}>{value}</div>
    </div>
  );
}

function RelatedBlock({ items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="label-overline mb-2">Related muscles</div>
      <div className="flex flex-wrap gap-1.5" data-testid="panel-related">
        {items.map((id) => (
          <span
            key={`rel-${id}`}
            className="px-2.5 py-1 rounded-full bg-paper-100 text-ink-700 text-xs border border-paper-200"
          >
            {MUSCLE_LABELS[id] || id}
          </span>
        ))}
      </div>
    </div>
  );
}
